import { useState } from 'react'
import { Link } from 'react-router-dom'

import { ACTIVITY_TYPES, ACTIVITY_TYPE_LABELS, type ActivityType, type MarketObservation } from '@/api/market'
import { useNotLivePage } from '@/app/notLive'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, EmptyState, LoadingBlock } from '@/design'
import { formatDateKey } from '@/lib/filters'

import { ActivityTypeBadge, ConfidenceBadge, SourceBadge } from './badges'
import { useDrillDown, useMarketQuery } from './hooks'
import { MonitoringNotice } from './MonitoringNotice'
import { NotLiveSection } from './NotLiveSection'
import './market.css'

function ObservationItem({ observation, href }: { observation: MarketObservation; href: string }) {
  return (
    <li className="mk-activity-item">
      <div className="mk-activity-item__meta">
        <ActivityTypeBadge activityType={observation.activityType} />
        <SourceBadge source={observation.source} />
        <ConfidenceBadge confidence={observation.confidence} />
        <span className="mk-activity-item__time">{formatDateKey(observation.occurredAt, true)}</span>
      </div>
      <p className="mk-activity-item__fact">{observation.fact}</p>
      {observation.competitorName ? (
        <Link to={href} className="mk-activity-item__link">
          {observation.competitorName}
        </Link>
      ) : null}
    </li>
  )
}

/** Every dated observation about a tracked competitor, newest first, across the selected period. */
export function ActivityPage({ route }: { route: RouteDef }) {
  const notLive = useNotLivePage('marketObservations')
  const observations = useMarketQuery('observations', (client, filters) => client.marketObservations(filters))
  const href = useDrillDown()
  const [filter, setFilter] = useState<ActivityType | 'all'>('all')

  const rows = (observations.data?.data ?? []).filter((observation) => filter === 'all' || observation.activityType === filter)

  if (notLive) return <NotLiveSection route={route} />

  return (
    <SectionFrame route={route}>
      <div className="ci-stack">
        <MonitoringNotice />

        <Card
          title="Activity"
          subtitle="New launches, closing soon, price changes and category trends"
          busy={observations.isFetching}
          actions={
            <label className="mk-activity-filter">
              <span className="ci-visually-hidden">Filter by activity type</span>
              <select value={filter} onChange={(event) => setFilter(event.target.value as ActivityType | 'all')}>
                <option value="all">All activity</option>
                {ACTIVITY_TYPES.map((activityType) => (
                  <option key={activityType} value={activityType}>
                    {ACTIVITY_TYPE_LABELS[activityType]}
                  </option>
                ))}
              </select>
            </label>
          }
        >
          {observations.isPending ? <LoadingBlock rows={6} /> : null}
          {observations.isError ? <EmptyState variant="outage" compact description="Activity could not be loaded." /> : null}
          {observations.data ? (
            rows.length > 0 ? (
              <ul className="mk-activity-list" aria-label="Market activity">
                {rows.map((observation) => (
                  <ObservationItem key={observation.id} observation={observation} href={href(observation.drillDown)} />
                ))}
              </ul>
            ) : (
              <EmptyState variant="no-results" compact title="Nothing matches this filter" description="Try a different activity type or a wider date range." />
            )
          ) : null}
        </Card>
      </div>
    </SectionFrame>
  )
}
